import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { api } from '../../lib/api';
import { esMovil } from '../../lib/utils';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Camera, Upload, Loader2, GitCompare, ImagePlus, Ruler } from 'lucide-react';
import { CATEGORIAS, VALORES_DE_CATEGORIA, dec, fechaCorta } from '../../lib/oftalmo';
import VisorImagen, { urlImagen } from './VisorImagen';
import Comparador from './Comparador';
import GraficaOjos from './GraficaOjos';

// La medida que resume cada serie en su gráfica.
const MEDIDA_DE_SERIE = {
  oct_macula: ['cst', 'Grosor central', 'µm'],
  oct_nervio: ['cfnr', 'CFNR', 'µm'],
  topografia: ['kmax', 'Kmax', 'D'],
};

/**
 * Las imágenes del paciente por prueba y ojo, de la más antigua a la más
 * reciente, para ver la evolución de un vistazo. Se suben al momento:
 * arrastrando, pegando (Ctrl/⌘+V, una captura del aparato) o con la cámara
 * del móvil; varias a la vez.
 */
export default function ImagenesOftalmo({ pid, imagenes, puedeEditar, iaActiva, onCambio, categoriaInicial }) {
  const [cat, setCat] = useState(categoriaInicial || 'oct_macula');
  const [ojo, setOjo] = useState('OD');
  const [fecha, setFecha] = useState(new Date().toISOString().slice(0, 10));
  const [diag, setDiag] = useState('');
  const [subiendo, setSubiendo] = useState(0);
  const [arrastrando, setArrastrando] = useState(false);
  const [filtro, setFiltro] = useState('todas');
  const [visor, setVisor] = useState(null);
  const [marcadas, setMarcadas] = useState([]);
  const [comparar, setComparar] = useState(null);
  const input = useRef(null);
  const camara = useRef(null);

  const subir = async (archivos) => {
    const lista = Array.from(archivos || []).filter((f) => f.type.startsWith('image/') || /\.(jpe?g|png|heic|heif|webp|tiff?)$/i.test(f.name));
    if (!lista.length) return;
    setSubiendo(lista.length);
    let ok = 0;
    for (const archivo of lista) {
      const fd = new FormData();
      fd.append('file', archivo);
      fd.append('categoria', cat);
      fd.append('ojo', ojo);
      fd.append('fecha', fecha);
      fd.append('diagnostico', diag);
      try {
        // eslint-disable-next-line no-await-in-loop
        const r = await api.post(`/api/patients/${pid}/oftalmologia/imagenes`, fd, { timeout: 180000 });
        onCambio?.({ tipo: 'nueva', imagen: r.data });
        ok += 1;
      } catch (e) {
        toast.error(`${archivo.name}: ${e?.response?.data?.detail || 'no se pudo subir'}`);
      }
      setSubiendo((n) => n - 1);
    }
    if (ok) toast.success(ok === 1 ? 'Imagen añadida a la historia' : `${ok} imágenes añadidas a la historia`);
  };

  // Pegar una captura directamente (sin guardar el archivo antes).
  useEffect(() => {
    if (!puedeEditar) return undefined;
    const pegar = (e) => {
      if (visor || comparar) return;
      const t = e.target;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA')) return;
      const files = Array.from(e.clipboardData?.files || []);
      if (files.length) { e.preventDefault(); subir(files); }
    };
    window.addEventListener('paste', pegar);
    return () => window.removeEventListener('paste', pegar);
  });

  const series = useMemo(() => {
    const m = {};
    imagenes.filter((i) => filtro === 'todas' || i.categoria === filtro).forEach((i) => {
      const k = `${i.categoria}|${i.ojo || '—'}`;
      (m[k] = m[k] || []).push(i);
    });
    return Object.entries(m).map(([k, xs]) => {
      const [c, o] = k.split('|');
      return { clave: k, categoria: c, ojo: o, imagenes: xs.sort((a, b) => (a.fecha || '').localeCompare(b.fecha || '')) };
    }).sort((a, b) => (b.imagenes.at(-1).fecha || '').localeCompare(a.imagenes.at(-1).fecha || ''));
  }, [imagenes, filtro]);

  const marcar = (img) => {
    setMarcadas((p) => {
      if (p.includes(img.id)) return p.filter((x) => x !== img.id);
      const otra = imagenes.find((i) => i.id === p[0]);
      if (otra && (otra.categoria !== img.categoria || otra.ojo !== img.ojo)) return [img.id];
      return [...p, img.id].slice(-2);
    });
  };
  const abrirComparar = (ids) => {
    const xs = ids.map((id) => imagenes.find((i) => i.id === id)).sort((a, b) => (a.fecha || '').localeCompare(b.fecha || ''));
    setComparar(xs.map((x) => x.id));
  };

  return (
    <div className="space-y-4">
      {puedeEditar && (
        <div
          className={`cp-card p-4 border-2 border-dashed transition-colors ${arrastrando ? 'border-[#2E6E5E] bg-[rgba(46,110,94,0.06)]' : 'border-transparent'}`}
          onDragOver={(e) => { e.preventDefault(); setArrastrando(true); }}
          onDragLeave={() => setArrastrando(false)}
          onDrop={(e) => { e.preventDefault(); setArrastrando(false); subir(e.dataTransfer.files); }}
          data-testid="oft-subida"
        >
          <div className="flex flex-wrap items-end gap-3">
            <label className="text-xs text-muted-foreground flex flex-col gap-1">Prueba
              <select value={cat} onChange={(e) => setCat(e.target.value)} data-testid="oft-subida-categoria"
                className="h-9 rounded-md border border-input bg-background px-2 text-sm text-foreground">
                {Object.entries(CATEGORIAS).map(([k, t]) => <option key={k} value={k}>{t}</option>)}
              </select>
            </label>
            <div className="text-xs text-muted-foreground flex flex-col gap-1">Ojo
              <div className="flex rounded-md border overflow-hidden">
                {['OD', 'OI'].map((o) => (
                  <button key={o} type="button" onClick={() => setOjo(o)}
                    className={`px-3 h-9 text-sm font-semibold ${ojo === o ? (o === 'OD' ? 'bg-red-600 text-white' : 'bg-blue-600 text-white') : 'bg-background text-foreground'}`}>{o}</button>
                ))}
              </div>
            </div>
            <label className="text-xs text-muted-foreground flex flex-col gap-1">Fecha
              <Input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} className="h-9 w-40" />
            </label>
            <label className="text-xs text-muted-foreground flex flex-col gap-1 flex-1 min-w-[160px]">Diagnóstico (opcional)
              <Input value={diag} onChange={(e) => setDiag(e.target.value)} className="h-9" placeholder="DMAE húmeda, úlcera, queratocono…" />
            </label>
            <div className="flex gap-2">
              <Button onClick={() => input.current?.click()} disabled={subiendo > 0} data-testid="oft-subir">
                {subiendo ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
                {subiendo ? `Subiendo ${subiendo}…` : 'Subir imágenes'}
              </Button>
              {esMovil() && (
                <Button variant="outline" onClick={() => camara.current?.click()} disabled={subiendo > 0}>
                  <Camera className="w-4 h-4 mr-1" />Cámara
                </Button>
              )}
            </div>
            <input ref={input} type="file" accept="image/*,.heic,.heif,.tif,.tiff" multiple className="hidden"
              data-testid="oft-input-archivo" onChange={(e) => { subir(e.target.files); e.target.value = ''; }} />
            <input ref={camara} type="file" accept="image/*" capture="environment" className="hidden"
              onChange={(e) => { subir(e.target.files); e.target.value = ''; }} />
          </div>
          <div className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
            <ImagePlus className="w-3.5 h-3.5" />Arrastra aquí las imágenes del OCT, el retinógrafo o la lámpara, o pega una captura con Ctrl/⌘+V. Se guardan al momento en la historia.
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <select value={filtro} onChange={(e) => setFiltro(e.target.value)} className="h-9 rounded-md border border-input bg-background px-2 text-sm">
          <option value="todas">Todas las pruebas ({imagenes.length})</option>
          {Object.entries(CATEGORIAS).filter(([k]) => imagenes.some((i) => i.categoria === k)).map(([k, t]) => (
            <option key={k} value={k}>{t} ({imagenes.filter((i) => i.categoria === k).length})</option>
          ))}
        </select>
        <Button variant="outline" size="sm" disabled={marcadas.length !== 2} onClick={() => abrirComparar(marcadas)} data-testid="oft-comparar-marcadas">
          <GitCompare className="w-4 h-4 mr-1" />Comparar las 2 marcadas
        </Button>
        <span className="text-xs text-muted-foreground">Marca dos imágenes de la misma prueba y ojo para compararlas.</span>
      </div>

      {!series.length && <div className="cp-card p-10 text-center text-sm text-muted-foreground">Aún no hay imágenes.</div>}
      {series.map((s) => {
        const medida = MEDIDA_DE_SERIE[s.categoria];
        const conArea = s.imagenes.filter((i) => i.medida?.area_mm2 !== null && i.medida?.area_mm2 !== undefined);
        const clave = s.ojo === 'OI' ? 'oi' : 'od';
        const puntos = medida ? s.imagenes.filter((i) => i.valores?.[medida[0]] !== undefined)
          .map((i) => ({ fecha: i.fecha, [clave]: i.valores[medida[0]] })) : [];
        const puntosArea = conArea.map((i) => ({ fecha: i.fecha, [clave]: i.medida.area_mm2 }));
        return (
          <div key={s.clave} className="cp-card p-4" data-testid={`oft-serie-${s.categoria}-${s.ojo}`}>
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
              <div className="font-display font-semibold">
                {CATEGORIAS[s.categoria]} <span style={{ color: s.ojo === 'OD' ? '#DC2626' : '#2563EB' }}>{s.ojo}</span>
                <span className="text-xs text-muted-foreground font-normal ml-2">{s.imagenes.length} imagen{s.imagenes.length === 1 ? '' : 'es'} · {fechaCorta(s.imagenes[0].fecha)} → {fechaCorta(s.imagenes.at(-1).fecha)}</span>
              </div>
              {s.imagenes.length > 1 && (
                <Button size="sm" variant="outline" onClick={() => abrirComparar([s.imagenes[0].id, s.imagenes.at(-1).id])} data-testid={`oft-comparar-serie-${s.categoria}-${s.ojo}`}>
                  <GitCompare className="w-4 h-4 mr-1" />Comparar primera y última
                </Button>
              )}
            </div>
            <div className="flex gap-3 overflow-x-auto pb-2">
              {s.imagenes.map((img) => (
                <div key={img.id} className={`shrink-0 w-44 rounded-lg border-2 overflow-hidden ${marcadas.includes(img.id) ? 'border-[#2E6E5E]' : 'border-transparent'}`}>
                  <button type="button" className="block w-full bg-black" onClick={() => setVisor(img)} data-testid="oft-miniatura">
                    <img src={urlImagen(pid, img.id, 'mini')} alt="" className="w-full h-32 object-contain" loading="lazy" />
                  </button>
                  <div className="p-1.5 text-xs space-y-0.5 bg-[var(--cp-soft)] dark:bg-transparent">
                    <div className="flex items-center justify-between">
                      <b>{fechaCorta(img.fecha)}</b>
                      <label className="flex items-center gap-1 cursor-pointer"><input type="checkbox" checked={marcadas.includes(img.id)} onChange={() => marcar(img)} />comparar</label>
                    </div>
                    {(VALORES_DE_CATEGORIA[img.categoria] || []).filter(([k]) => img.valores?.[k] !== undefined).slice(0, 3).map(([k, t]) => (
                      <div key={k} className="truncate">{t.split(' (')[0]}: <b>{dec(img.valores[k], k === 'cst' || k === 'cfnr' || k === 'paqui_min' ? 0 : k === 'cd' ? 2 : 1)}</b></div>
                    ))}
                    {img.medida?.area_mm2 !== null && img.medida?.area_mm2 !== undefined && (
                      <div className="flex items-center gap-1"><Ruler className="w-3 h-3" /><b>{dec(img.medida.area_mm2, 2)} mm²</b></div>
                    )}
                    {img.diagnostico && <div className="truncate text-muted-foreground">{img.diagnostico}</div>}
                  </div>
                </div>
              ))}
            </div>
            {puntos.length > 1 && (
              <div className="mt-2">
                <div className="text-xs text-muted-foreground mb-1">{medida[1]} ({medida[2]})</div>
                <GraficaOjos puntos={puntos} unidad={medida[2]} alto={180} decimales={medida[2] === 'D' ? 1 : 0} />
              </div>
            )}
            {puntosArea.length > 1 && (
              <div className="mt-2" data-testid="oft-grafica-area">
                <div className="text-xs text-muted-foreground mb-1">Área de la lesión medida (mm²) · {conArea[0].medicion?.que || ''}</div>
                <GraficaOjos puntos={puntosArea} unidad="mm²" alto={180} decimales={2} dominio={[0, 'auto']} />
              </div>
            )}
          </div>
        );
      })}

      <VisorImagen pid={pid} imagen={visor} open={!!visor} onOpenChange={(o) => !o && setVisor(null)} puedeEditar={puedeEditar}
        onGuardada={(img) => { onCambio?.({ tipo: 'editada', imagen: img }); setVisor(img); }}
        onBorrada={(id) => onCambio?.({ tipo: 'borrada', id })} />
      {comparar && (
        <Comparador pid={pid} imagenes={imagenes} inicial={comparar} open={!!comparar} iaActiva={iaActiva} puedeEditar={puedeEditar}
          onOpenChange={(o) => !o && setComparar(null)} />
      )}
    </div>
  );
}
